import React, { useCallback, useEffect, useState } from 'react';
import {
  getFreightCustomerAddresses,
  getFreightOverrides,
  upsertFreightOverride,
  deleteFreightOverride,
} from '../../services/syncApi';

// Manual per-address zone overrides — the only way to flag an address as POA.
// Overrides bind to the EXACT disambiguated address_key the sync iterates
// (MAIN / BILLTO / ST:<num> / ST:<num>#N), surfaced via the customer preview.

const COUNTRIES = ['AU', 'NZ'];
const OVERRIDE_OPTIONS = [
  { value: '', label: 'Auto (postcode)' },
  { value: 'metro', label: 'Metro' },
  { value: 'regional', label: 'Regional' },
  { value: 'poa', label: 'POA' },
];

const errToText = (raw) => {
  if (!raw) return '';
  if (typeof raw === 'string') return raw;
  if (raw.message) return raw.message;
  if (Array.isArray(raw)) return raw.map((r) => r.msg || JSON.stringify(r)).join('; ');
  try { return JSON.stringify(raw); } catch { return String(raw); }
};
const formatErr = (e) => errToText(e?.response?.data?.detail || e?.message || 'request failed');

const FreightOverrides = ({ onError }) => {
  const [company, setCompany] = useState('AU');
  const [custNum, setCustNum] = useState('');
  const [addresses, setAddresses] = useState(null);
  const [loadingAddrs, setLoadingAddrs] = useState(false);
  const [busyKey, setBusyKey] = useState(null);

  const [overrides, setOverrides] = useState([]);
  const [loadingList, setLoadingList] = useState(true);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const data = await getFreightOverrides();
      setOverrides(data);
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setLoadingList(false);
    }
  }, [onError]);

  useEffect(() => { loadList(); }, [loadList]);

  const lookup = async () => {
    const n = Number(custNum);
    if (!custNum || !Number.isInteger(n)) { onError('Enter a numeric CustNum'); return; }
    setLoadingAddrs(true);
    setAddresses(null);
    onError(null);
    try {
      const data = await getFreightCustomerAddresses(company, n);
      setAddresses(data.addresses || []);
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setLoadingAddrs(false);
    }
  };

  const setOverride = async (addressKey, value) => {
    const n = Number(custNum);
    setBusyKey(addressKey);
    onError(null);
    try {
      if (value === '') {
        await deleteFreightOverride(company, n, addressKey);
      } else {
        await upsertFreightOverride({
          company, cust_num: n, address_key: addressKey, zone_override: value,
        });
      }
      await lookup();
      await loadList();
    } catch (e) {
      onError(formatErr(e));
    } finally {
      setBusyKey(null);
    }
  };

  const clearListed = async (row) => {
    onError(null);
    try {
      await deleteFreightOverride(row.company, row.cust_num, row.address_key);
      await loadList();
      if (Number(custNum) === row.cust_num && company === row.company) await lookup();
    } catch (e) {
      onError(formatErr(e));
    }
  };

  return (
    <section className="card">
      <h2>POA &amp; zone overrides</h2>
      <p className="muted">
        Override the postcode-derived zone for a specific ship-to address — including flagging it
        as <strong>POA</strong>. Look up a customer to see their addresses with the exact key the
        sync uses.
      </p>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0.75rem', marginBottom: '0.75rem' }}>
        <label className="muted" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          Company
          <select value={company} onChange={(e) => setCompany(e.target.value)}>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="muted" style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
          CustNum
          <input type="number" value={custNum} onChange={(e) => setCustNum(e.target.value)}
            placeholder="e.g. 12345" style={{ width: '9rem' }} />
        </label>
        <button type="button" className="primary" onClick={lookup} disabled={loadingAddrs}>
          {loadingAddrs ? 'Loading…' : 'Look up'}
        </button>
      </div>

      {addresses && (
        addresses.length === 0 ? (
          <p className="muted">No addresses for this customer.</p>
        ) : (
          <table className="value-lookups-table">
            <thead>
              <tr>
                <th>Address key</th>
                <th>Address</th>
                <th>Postcode</th>
                <th>Country</th>
                <th>Resolved type</th>
                <th>Override</th>
              </tr>
            </thead>
            <tbody>
              {addresses.map((a) => (
                <tr key={a.address_key}>
                  <td><code>{a.address_key}</code></td>
                  <td>
                    {a.address_line1 || a.label || <span className="muted">—</span>}
                    {a.city ? <span className="muted"> · {a.city}</span> : null}
                  </td>
                  <td>{a.zip || <span className="muted">—</span>}</td>
                  <td>{a.country_code}</td>
                  <td>{a.type || <span className="muted">—</span>}</td>
                  <td>
                    <select
                      value={a.override || ''}
                      onChange={(e) => setOverride(a.address_key, e.target.value)}
                      disabled={busyKey === a.address_key}
                    >
                      {OVERRIDE_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      )}

      <h3 style={{ marginTop: '1.25rem' }}>Existing overrides</h3>
      {loadingList ? (
        <p className="muted">Loading…</p>
      ) : (
        <table className="value-lookups-table">
          <thead>
            <tr>
              <th>Company</th>
              <th>CustNum</th>
              <th>Address key</th>
              <th>Override</th>
              <th>Note</th>
              <th>Updated</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {overrides.map((row) => (
              <tr key={`${row.company}|${row.cust_num}|${row.address_key}`}>
                <td>{row.company}</td>
                <td>{row.cust_num}</td>
                <td><code>{row.address_key}</code></td>
                <td>{row.zone_override}</td>
                <td>{row.note || <span className="muted">—</span>}</td>
                <td className="muted">
                  {row.updated_at
                    ? `${new Date(row.updated_at).toLocaleString()}${row.updated_by ? ` · ${row.updated_by}` : ''}`
                    : '—'}
                </td>
                <td>
                  <button type="button" className="ghost" onClick={() => clearListed(row)}>Clear</button>
                </td>
              </tr>
            ))}
            {overrides.length === 0 && (
              <tr><td colSpan={7} className="muted">No overrides set.</td></tr>
            )}
          </tbody>
        </table>
      )}
    </section>
  );
};

export default FreightOverrides;
